// The Home surface's answer to "is anything waiting on me?".
//
// Every portal Home leads with a next-action card that only ever knows about
// money and lifecycle stage — it cannot see outstanding document requests, and
// on the buyer Home that produced a screen telling someone there was nothing
// for them to do while ten items sat pending. This strip is the missing half:
// it renders as a SIBLING directly beneath that card, in the same position on
// every branch, so the card's own logic never has to grow a requests arm and
// no branch can be displaced by one.
//
// It reads the SHARED polled badge context rather than fetching. That is the
// whole point — the sidebar badge and this strip must never show numbers that
// disagree, and a second fetch on a 30s poller is exactly how they would.
//
// Portal rules: no React Query; design-system + portal-local imports only.

import { ArrowRight, Clock, Inbox } from "lucide-react";
import { Link } from "react-router-dom";
import { useRequestsBadge } from "./PortalRequestsBadgeContext";

/** Where the strip sends someone. Home tabs drive tab state through `?tab=`,
 *  so a relative query link is the portal's established navigation for this
 *  (the Brokerage attention card uses the same shape). */
const REQUESTS_HREF = "?tab=requests";

export interface PortalRequestsStripProps {
  /** Overrides the "N items need you" line. Receives the count so a portal can
   *  say something more specific without re-implementing pluralisation. */
  needsYouLabel?: (count: number) => string;
  /** Overrides the waiting-on-us line. */
  underReviewLabel?: (count: number) => string;
  testId?: string;
}

function defaultNeedsYou(count: number): string {
  return count === 1 ? "1 item needs you" : `${count} items need you`;
}

function defaultUnderReview(count: number): string {
  return count === 1
    ? "We're reviewing the item you sent — nothing needed from you right now."
    : `We're reviewing the ${count} items you sent — nothing needed from you right now.`;
}

export function PortalRequestsStrip({
  needsYouLabel = defaultNeedsYou,
  underReviewLabel = defaultUnderReview,
  testId = "portal-requests-strip",
}: PortalRequestsStripProps) {
  const { counts } = useRequestsBadge();
  const { needsYou, underReview } = counts;

  // Nothing outstanding in either direction renders NOTHING — no empty state,
  // no placeholder, no reserved space. A Home with nothing waiting should look
  // like a Home with nothing waiting.
  if (needsYou <= 0 && underReview <= 0) return null;

  const actionable = needsYou > 0;
  const Icon = actionable ? Inbox : Clock;

  // Both states link to Requests, including the waiting-on-us one. For buyer and
  // seller the sidebar badge now counts `needsYou`, so a recipient whose items
  // are all under review sees no badge at all — this link is the only way in
  // from Home, and the Requests header breaks the figure down on arrival.
  return (
    <Link
      to={REQUESTS_HREF}
      data-testid={testId}
      className="group flex items-center gap-space-3 rounded-md border border-border-light bg-white-pure p-space-3 text-small text-onyx no-underline hover:border-gold hover:text-gold-shadow"
    >
      <Icon
        size={18}
        strokeWidth={1.5}
        className={`shrink-0 ${actionable ? "text-gold" : "text-gray-mid"}`}
        aria-hidden
      />
      <span className="flex-1">
        {actionable ? (
          <>
            <span className="font-semibold" data-testid={`${testId}-needs-you`}>
              {needsYouLabel(needsYou)}
            </span>
            {underReview > 0 && (
              <span className="block text-caption text-gray-mid" data-testid={`${testId}-under-review`}>
                {underReview === 1 ? "1 more under review" : `${underReview} more under review`}
              </span>
            )}
          </>
        ) : (
          <span data-testid={`${testId}-under-review`}>{underReviewLabel(underReview)}</span>
        )}
      </span>
      <ArrowRight
        size={16}
        strokeWidth={1.5}
        className="shrink-0 text-gray-mid group-hover:text-gold-shadow"
        aria-hidden
      />
    </Link>
  );
}
